import { useMutation, useQuery } from '@apollo/client';
import { Box, Button } from '@mui/material';
import { FileType } from '@procureai/constellation/foundation/file';
import { Restricted } from '@procureai/permissions-ui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { type FileType as StoredFileVariant, useFileStorage } from '@procureai/uix';
import { isNotNil, remove } from 'ramda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgChatMessageStatus, SvbrgBackgroundJobStatus } from '@/generated/gql/graphql';
import { FeatureFlag, I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { UploadDropZone } from '~/components/FileUpload/UploadDropZone/UploadDropZone';
import { UploadedFileListItem } from '~/components/FileUpload/UploadedFileListItem/UploadedFileListItem';
import { UploadErrors } from '~/components/FileUpload/UploadErrorList/UploadErrorList';
import { ProgressIndicatorAlternating } from '~/components/ProgressIndicator/ProgressIndicatorAlternating';
import { useBatchedFileUpload } from '~/hooks/useMultiFileUploadProgress/useMultiFileUploadProgress';
import { useChatSession } from '~/modules/Module.hooks';
import { createUploadIndicatorAlternatingProps as createProgressIndicatorProps } from '~/routes/IntakeDetail/steps/IntakeQuoteUploadStep/IntakeQuoteUploadStep.settings';

import {
  INTAKE_QUOTE_UPLOAD_SKIP_MUTATION,
  INTAKE_QUOTE_UPLOAD_STEP_MUTATION,
} from './IntakeQuoteUploadStep.mutation.gql';
import { INTAKE_QUOTE_ANALYSIS } from './IntakeQuoteUploadStep.query.gql';
import { INTAKE_UPDATE_QUOTE_ANALYSIS } from './IntakeQuoteUploadStep.subscription.gql';

type QuoteAnalysisRun = {
  id?: string;
  fileId: string;
  status?: SvbrgBackgroundJobStatus;
};

export const IntakeQuoteUploadStep = ({ status, role, createdBy, isCompleted }: ChatMessageProps) => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeQuoteUploadStep' });

  const session = useChatSession();

  const [errors, setErrors] = useState<string[]>([]);

  const { onError } = useCustomSnackbar();
  const { upload: falconUpload } = useFileStorage();

  const {
    subscribeToMore,
    data,
    loading: isLoading,
  } = useQuery(INTAKE_QUOTE_ANALYSIS, {
    variables: session,
  });
  const analysis = data?.sevenbridges?.chatSession?.analysis;

  if (analysis?.id && analysis?.status !== SvbrgBackgroundJobStatus.COMPLETED) {
    subscribeToMore({
      document: INTAKE_UPDATE_QUOTE_ANALYSIS,
      variables: session,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        // Object.assign used to comply with typescript only
        return Object.assign({}, prev, subscriptionData.data);
      },
      onError,
    });
  }

  const [analyse] = useMutation(INTAKE_QUOTE_UPLOAD_STEP_MUTATION, {
    onError,
    refetchQueries: [{ query: INTAKE_QUOTE_ANALYSIS, variables: session }],
  });

  const [skip, { loading: loadingSkip }] = useMutation(INTAKE_QUOTE_UPLOAD_SKIP_MUTATION, {
    onError,
    variables: {
      data: session,
    },
  });

  const upload = async (file: File): Promise<QuoteAnalysisRun> => {
    // uix types variants as ATTACHMENT | PROFILE_PICTURE | OTHER, but its Falcon adapter forwards
    // QUOTE unchanged and Falcon still routes it to quote analysis.
    const { error, data } = await falconUpload({
      data: { file, variant: FileType.QUOTE as unknown as StoredFileVariant },
    });
    if (error) {
      // throwing custom error as those are handled by useMultiFileUploadProgress hook
      throw new Error(t('upload.error'));
    }

    await analyse({
      variables: { data: { fileId: data.uploadId, chatSessionId: session.id, fileName: file.name } },
      refetchQueries: [{ query: INTAKE_QUOTE_ANALYSIS, variables: session }],
    });

    return {
      fileId: data.uploadId,
      status: SvbrgBackgroundJobStatus.IN_PROGRESS,
    };
  };

  const { uploadBatch, isLoading: isUploading } = useBatchedFileUpload<QuoteAnalysisRun>(upload);
  const fileId = analysis?.fileId;

  const hasRunningAnalysis = (analysis && analysis.status) === SvbrgBackgroundJobStatus.IN_PROGRESS;
  const isFailedAnalysis = isNotNil(analysis) && analysis.status === SvbrgBackgroundJobStatus.FAILED;
  const disabledSkip = isCompleted || hasRunningAnalysis || loadingSkip;
  const disableUpload = isCompleted || isUploading;

  const shouldRenderDropZone = !isLoading && ((isFailedAnalysis && fileId) || !fileId);
  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={
        isUploading || hasRunningAnalysis || status === SvbrgChatMessageStatus.PENDING ? (
          <ProgressIndicatorAlternating {...createProgressIndicatorProps(t)} />
        ) : (
          t('message')
        )
      }
      footer={
        <Restricted
          fallback={
            <Button
              onClick={() => {
                skip();
              }}
              disabled={disabledSkip}
            >
              {t('button.skip')}
            </Button>
          }
          needs={{ feature: FeatureFlag.HIDE_SKIP_UPLOAD_QUOTE_BUTTON }}
        />
      }
    >
      {shouldRenderDropZone && (
        <UploadDropZone
          onDrop={uploadBatch}
          disabled={disableUpload}
          accept={{ 'application/pdf': ['.pdf'] }}
          buttonLabel={t('button.upload')}
          onDropErrors={(errorMessages) => setErrors(errorMessages)}
        />
      )}
      <UploadErrors errors={errors} onDiscard={(index) => setErrors(remove(index, 1))} />
      {!isUploading && fileId && !isFailedAnalysis && (
        <Box
          sx={{
            paddingTop: 2,
          }}
        >
          <UploadedFileListItem fileId={fileId} />
        </Box>
      )}
    </ChatStep>
  );
};
